/**
 * Technical SEO for omens.com.br (robots.txt, sitemap, www → apex). Until 2026-10 Omens fell back to the
 * static public/robots.txt, which points crawlers at the Souisha sitemap, and the sitemap listed only the home.
 * No module-alias imports: the middleware and the API handler specs load it.
 */

import { OMENS_PAGES } from './pages'

export type { OmensPageSeo } from './pages'
export { OMENS_PAGES } from './pages'

export const OMENS_ORIGIN = 'https://omens.com.br'

/**
 * Regex source of the host-scoped rewrites in `next.config.js` (`has: [{ type: 'host', value }]`). Next
 * anchors it and matches it against the lower-cased hostname without the port. omens.fit and the other
 * brands keep their current robots.txt.
 */
export const OMENS_HOST_REWRITE_PATTERN = '(?:www\\.)?omens\\.com\\.br'

const toHostname = (host: string | string[] | null | undefined): string => {
  const value = Array.isArray(host) ? host[0] : host
  return (value ?? '').split(':', 1)[0].trim().toLowerCase()
}

export const isOmensHost = (host: string | string[] | null | undefined): boolean =>
  ['omens.com.br', 'www.omens.com.br'].includes(toHostname(host))

/**
 * AI search/answer crawlers explicitly allowed (GEO). A bot with its own group ignores `User-agent: *`,
 * so every group repeats the same Disallow lines.
 */
export const OMENS_AI_CRAWLERS = [
  'GPTBot',
  'OAI-SearchBot',
  'ChatGPT-User',
  'ClaudeBot',
  'Claude-SearchBot',
  'PerplexityBot',
  'Google-Extended'
]

/**
 * The logged-in patient area (the academy student area lives under /me too). The purchase flows
 * (/academy, /onboarding) are NOT blocked: they answer `X-Robots-Tag: noindex`, which crawlers must fetch to see.
 */
const OMENS_DISALLOW = ['/me']

export const buildOmensRobotsTxt = (): string =>
  [
    ...['*', ...OMENS_AI_CRAWLERS].flatMap((agent) => [
      `User-agent: ${agent}`,
      'Allow: /',
      ...OMENS_DISALLOW.map((path) => `Disallow: ${path}`),
      ''
    ]),
    `Sitemap: ${OMENS_ORIGIN}/sitemap.xml`,
    // The WordPress blog (Yoast) keeps its own sitemap.
    `Sitemap: ${OMENS_ORIGIN}/blog/sitemap_index.xml`,
    ''
  ].join('\n')

/**
 * The sitemap: every page of `OMENS_PAGES` that is its own canonical and indexable, with its `lastmod`. Left out
 * on purpose: the duplicates (canonical to another page) and /consultar (noindex), other brands' pages (Isha,
 * MedLeve, Cuidei), paid/campaign landings, logged-in and purchase flows, the noindex pages and
 * the WordPress blog.
 */
export const OMENS_SITEMAP_PAGES: ReadonlyArray<readonly [path: string, lastmod: string]> = Object.entries(
  OMENS_PAGES
).flatMap(([path, page]) => (page.canonical || page.noindex ? [] : [[path, page.lastmod] as const]))

export const buildOmensSitemapXml = (): string => {
  const urls = OMENS_SITEMAP_PAGES.map(
    ([path, lastmod]) => `  <url>
    <loc>${OMENS_ORIGIN}${path}</loc>
    <lastmod>${lastmod}</lastmod>
  </url>`
  ).join('\n')
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>`
}

/**
 * www.omens.com.br → omens.com.br (301), for the public pages and /llms.txt only: the session lives in
 * per-origin localStorage, so /me, /onboarding, /academy and the paid funnels stay on the host the visitor is on.
 */
export const omensApexRedirect = (host: string | null | undefined, path: string): string | undefined =>
  toHostname(host) === 'www.omens.com.br' &&
  (path === '/llms.txt' || Object.prototype.hasOwnProperty.call(OMENS_PAGES, path))
    ? `${OMENS_ORIGIN}${path}`
    : undefined
